import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensors,
  useSensor,
  MeasuringStrategy,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Box } from '@osmosis/react-ui/components/Box';

import { ItemsI, DnDListProps } from '../types';
import { SortableItem } from './SortableItem';
import {
  dropAnimation,
  getIndex,
  mapInitialItems,
  mapItems,
  onDragEnd,
  onDragOver,
  PLACEHOLDER_ID,
} from '../utils';
import { useCollisionDetectionStrategy } from '../hooks';
import { Container } from './Container';
import { Item } from '../Item';
import { DnDOverlay } from '../DnDOverlay';

/**
 * DnDList.
 */
export function DnDList({
  adjustScale = false,
  items: _initialItems = {},
  modifiers,
  strategy = verticalListSortingStrategy,
  vertical = false,
  styles = {},
  customRenders = {},
  getItems,
}: DnDListProps) {
  const {
    sx,
    containerSx,
    listsSx,
    itemSx,
    itemOrderedSx,
    itemTextSx,
    itemDraggedSx,
    itemDraggedOrderedSx,
    itemDraggedTextSx,
    containerWrapperSx,
    containerHeaderSx,
  } = styles;
  const initialItems = useMemo(
    () => mapInitialItems(_initialItems),
    [_initialItems],
  );
  const [items, setItems] = useState<ItemsI>(() => mapItems(initialItems));
  const lastOverId = useRef<UniqueIdentifier | null>(null);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [containers, setContainers] = useState(
    Object.keys(initialItems) as UniqueIdentifier[],
  );
  const recentlyMovedToNewContainer = useRef(false);
  const [clonedItems, setClonedItems] = useState<ItemsI | null>(null);

  const collisionDetectionStrategy = useCollisionDetectionStrategy({
    // FIXME: any
    activeId: activeId as any,
    lastOverId: lastOverId as any,
    items,
    recentlyMovedToNewContainer,
  });

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  useEffect(() => {
    getItems?.(items);
    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false;
    });
  }, [items]);

  useEffect(() => {
    setContainers(Object.keys(initialItems));
    setItems(mapItems(initialItems));
  }, [initialItems]);

  if (containers[0] !== Object.keys(initialItems)[0]) {
    // reset initialStates
    return null;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      onDragStart={({ active }) => {
        setActiveId(active.id);
        setClonedItems(items);
      }}
      onDragOver={({ active, over }) => {
        onDragOver({
          active,
          over: over as any, // FIXME: any
          setItems,
          recentlyMovedToNewContainer,
          items,
        });
      }}
      onDragEnd={({ active, over }) => {
        onDragEnd({
          overId: over?.id as any, // FIXME: any
          activeId: active.id,
          setContainers,
          setItems,
          setActiveId,
          items,
        });
      }}
      onDragCancel={() => {
        clonedItems && setItems(clonedItems);
        setActiveId(null);
        setClonedItems(null);
      }}
      modifiers={modifiers}
    >
      <Box
        sx={{
          display: 'inline-grid',
          boxSizing: 'border-box',
          gridAutoFlow: vertical ? 'row' : 'column',
          ...sx,
        }}
      >
        <SortableContext
          items={[...containers, PLACEHOLDER_ID]}
          strategy={
            vertical
              ? verticalListSortingStrategy
              : horizontalListSortingStrategy
          }
        >
          {containers.map((containerId) => (
            <Container
              key={containerId}
              id={containerId}
              items={items[containerId]}
              sx={containerSx}
              wrapperSx={containerWrapperSx}
              listsSx={listsSx}
              headerSx={containerHeaderSx}
            >
              <SortableContext items={items[containerId]} strategy={strategy}>
                {items[containerId].map((value, index) => {
                  return (
                    <SortableItem
                      customRender={customRenders.item}
                      ordered={initialItems[containerId].params?.ordered}
                      key={value}
                      value={value}
                      index={index}
                      sx={itemSx}
                      textSx={itemTextSx}
                      orderedSx={itemOrderedSx}
                    />
                  );
                })}
              </SortableContext>
            </Container>
          ))}
        </SortableContext>
      </Box>
      <DnDOverlay adjustScale={adjustScale} dropAnimation={dropAnimation}>
        {activeId && (
          <Item
            as="li"
            customRender={customRenders.item}
            dragOverlay
            index={getIndex(activeId, items)}
            value={activeId}
            ordered={initialItems[activeId]?.params?.ordered}
            sx={{
              ...itemSx,
              ...itemDraggedSx,
            }}
            textSx={{
              ...itemTextSx,
              ...itemDraggedTextSx,
            }}
            orderedSx={{
              ...itemOrderedSx,
              ...itemDraggedOrderedSx,
            }}
          />
        )}
      </DnDOverlay>
    </DndContext>
  );
}
